import { listAdmissions } from "@/core/admissions/admission-store";
import type { Admission, AdmissionChannel, TriageLevel } from "@/core/admissions/types";
import { TRIAGE_SHORT } from "@/core/admissions/types";
import { listPharmacyQueuePrescriptions } from "@/core/clinical/clinical-store";
import { getPendingDiagnosticsStats } from "@/core/imaging/imaging-store";
import {
  countOccupiedBeds,
  listBedsWithLocation,
} from "@/core/hospitalization/hospitalization-store";
import {
  formatDayLabel,
  isSameDay,
  startOfDay,
  type BreakdownPoint,
} from "./analytics-utils";

export interface AdmissionsDayPoint {
  label: string;
  date: string;
  admissions: number;
  emergency: number;
  total: number;
}

export interface OperationalDashboardAnalytics {
  patientsToday: number;
  admissionsToday: number;
  emergencyToday: number;
  orientedToday: number;
  pendingBillingToday: number;
  bedOccupancyPct: number;
  occupiedBeds: number;
  totalBeds: number;
  pharmacyQueue: number;
  labPending: number;
  imagingPending: number;
  avgOrientationMinutes: number | null;
  admissionsLast7Days: AdmissionsDayPoint[];
  destinationRotation: BreakdownPoint[];
  triageToday: BreakdownPoint[];
  channelMixMonth: BreakdownPoint[];
}

function admissionsInRange(admissions: Admission[], start: Date, end: Date): Admission[] {
  return admissions.filter((a) => {
    const at = new Date(a.createdAt);
    return at >= start && at < end;
  });
}

function countDestinations(admissions: Admission[]): BreakdownPoint[] {
  const map = new Map<string, number>();
  for (const adm of admissions) {
    const destinations = adm.destinations.length
      ? adm.destinations
      : adm.destination
        ? [adm.destination]
        : [];
    for (const dest of destinations) {
      const label = dest.trim();
      if (!label) continue;
      map.set(label, (map.get(label) ?? 0) + 1);
    }
  }
  return [...map.entries()]
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 10);
}

function countTriage(admissions: Admission[]): BreakdownPoint[] {
  const levels: TriageLevel[] = ["red", "yellow", "green"];
  return levels.map((level) => ({
    name: TRIAGE_SHORT[level],
    value: admissions.filter((a) => a.triageLevel === level).length,
  }));
}

function countChannelMix(admissions: Admission[]): BreakdownPoint[] {
  const channels: { key: AdmissionChannel; label: string }[] = [
    { key: "admission", label: "Admissions" },
    { key: "emergency", label: "Urgences" },
  ];
  return channels.map(({ key, label }) => ({
    name: label,
    value: admissions.filter((a) => a.channel === key).length,
  }));
}

function avgOrientationMinutes(admissions: Admission[]): number | null {
  const oriented = admissions.filter((a) => a.orientedAt);
  if (!oriented.length) return null;
  const totalMs = oriented.reduce((sum, a) => {
    const start = new Date(a.createdAt).getTime();
    const end = new Date(a.orientedAt!).getTime();
    return sum + Math.max(0, end - start);
  }, 0);
  return Math.round(totalMs / oriented.length / 60_000);
}

export function buildOperationalDashboardAnalytics(input?: {
  admissions?: Admission[];
}): OperationalDashboardAnalytics {
  const all = input?.admissions ?? listAdmissions();
  const now = startOfDay(new Date());
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  const today = admissionsInRange(all, now, tomorrow);
  const weekStart = new Date(now);
  weekStart.setDate(weekStart.getDate() - 6);
  const weekAdmissions = admissionsInRange(all, weekStart, tomorrow);
  const monthAdmissions = admissionsInRange(all, monthStart, tomorrow);

  const totalBeds = listBedsWithLocation().length;
  const occupiedBeds = countOccupiedBeds();
  const bedOccupancyPct = totalBeds > 0 ? Math.round((occupiedBeds / totalBeds) * 100) : 0;

  const diagnostics = getPendingDiagnosticsStats();

  const admissionsLast7Days: AdmissionsDayPoint[] = [];
  for (let i = 6; i >= 0; i -= 1) {
    const day = new Date(now);
    day.setDate(day.getDate() - i);
    const next = new Date(day);
    next.setDate(next.getDate() + 1);
    const dayAdm = admissionsInRange(all, day, next);
    admissionsLast7Days.push({
      label: formatDayLabel(day),
      date: day.toDateString(),
      admissions: dayAdm.filter((a) => a.channel === "admission").length,
      emergency: dayAdm.filter((a) => a.channel === "emergency").length,
      total: dayAdm.length,
    });
  }

  const emergencyToday = today.filter((a) => a.channel === "emergency");

  return {
    patientsToday: new Set(today.map((a) => a.patientId)).size,
    admissionsToday: today.filter((a) => a.channel === "admission").length,
    emergencyToday: emergencyToday.length,
    orientedToday: today.filter((a) => a.status === "oriented").length,
    pendingBillingToday: today.filter((a) => a.status === "pending_billing").length,
    bedOccupancyPct,
    occupiedBeds,
    totalBeds,
    pharmacyQueue: listPharmacyQueuePrescriptions().length,
    labPending: diagnostics.labPending,
    imagingPending: diagnostics.imagingPending,
    avgOrientationMinutes: avgOrientationMinutes(
      today.filter((a) => a.orientedAt && isSameDay(new Date(a.orientedAt), now)),
    ),
    admissionsLast7Days,
    destinationRotation: countDestinations(weekAdmissions),
    triageToday: countTriage(emergencyToday),
    channelMixMonth: countChannelMix(monthAdmissions),
  };
}
